import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ShoppingBag, MessageCircle } from "lucide-react";
import { addToCart } from "@/lib/cart";
import { getConfig } from "@/lib/config";
import { apiFetch } from "@/lib/api";
import { toast } from "sonner";

const EASE = [0.22, 1, 0.36, 1];

const staticProducts = [
  {
    n: "01",
    name: "Pooja Ghee",
    tag: "Pure · Slow-churned",
    desc: "Golden cow ghee, churned patiently the traditional way — made to feed the sacred flame so it burns steady, bright and fragrant through every aarti.",
    img: "https://images.unsplash.com/photo-1573812461383-e5f8b759d12e?fm=jpg&q=85&w=1600&auto=format&fit=crop",
  },
  {
    n: "02",
    name: "Flower & Puja Oils",
    tag: "Cold-pressed",
    desc: "More than fragrance — pure essential and carrier oils, cold-pressed from wild blossoms to leave their benefits on skin and hair. CDSCO certified, so every drop is as safe as it is sacred.",
    img: "/assets/flower-oils.jpg",
  },
  {
    n: "03",
    name: "Match Sticks",
    tag: "The first spark",
    desc: "Every aarti begins with a single spark. Our clean-burning match sticks light the diya in one strike — no struggle before the divine.",
    img: "/assets/matchsticks.jpg",
  },
  {
    n: "04",
    name: "Clay Toys",
    tag: "Mitti ki khushboo",
    desc: "Celebrating childhood from the ancient days into the modern world — toys made with earthy characteristics, shaped by hand from river clay.",
    img: "/assets/clay-toys.jpg",
  },
  {
    n: "05",
    name: "Sacred Handicrafts",
    tag: "Made by hand",
    desc: "Purely handmade by hands unreachable yet teachable — simple hands-on art in brass, terracotta and wood, shaped with a vision and finished with devotion.",
    img: "/assets/handicrafts.jpg",
  },
  {
    n: "06",
    name: "Laxwedeepan Kapoor",
    tag: "हिन्दू कपूर · Sacred Camphor",
    desc: "Camphor for sacred Hindu rituals, offerings in some Buddhist traditions, and space-cleansing practices inspired by Feng Shui. We call it pure only because our composition is verified — honestly labelled, always.",
    img: "/assets/kapoor.jpg",
  },
  {
    n: "07",
    name: "Indian Spices",
    tag: "मसाले · Culinary Heritage",
    desc: "Laxwedeepan brings the rich flavours and aromas of Indian spices to your kitchen — black and green cardamom, cinnamon, soya badi and many more raw, authentic, aromatic spices. Rooted in tradition, our collection celebrates India's culinary heritage — from everyday meals to festive feasts, discover the warmth of spices in every dish.",
    img: "/assets/indian-spices.jpg",
  },
  {
    n: "08",
    name: "Edible Ghee",
    tag: "For the Kitchen · 500 ml",
    desc: "The same slow-churned golden ghee, for your table — rich, aromatic and honest. Drizzle it over dal, rotis and festive sweets. 500 ml jar.",
    img: "/assets/edible-ghee.jpg",
    price: 800,
    stock: 50,
  },
  {
    n: "09",
    name: "Maa Durga Murti",
    tag: "Handcrafted · White & Gold",
    desc: "Maa Durga upon her lion — handcrafted, hand-painted in white and gold, and blessed before she leaves our workshop for your home temple.",
    img: "/assets/durga-murti-real.png",
    price: 2500,
    stock: 10,
  },
  {
    n: "10",
    name: "Shri Ganesh Murti",
    tag: "Handcrafted · White & Gold",
    desc: "Vighnaharta in white and gold — handcrafted and hand-painted, the first presence in every new beginning.",
    img: "/assets/ganesh-murti.png",
    price: 2500,
    stock: 10,
  },
];

const ProductRow = ({ p, i, checkoutOn }) => {
  const flip = i % 2 === 1;
  const n = String(i + 1).padStart(2, "0");
  // Anything that can't go in the cart (checkout off, unpriced, sold out) gets the WhatsApp button
  const canBuy = checkoutOn && p.price != null && p.id && p.stock !== 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 1, ease: EASE }}
      className={`group grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-16 ${i > 0 ? "mt-24 md:mt-32" : ""}`}
      data-testid={`product-${n}`}
    >
      <div className={`md:col-span-5 ${flip ? "md:order-2 md:col-start-8" : ""}`}>
        <a href="#contact" data-testid={`product-enquire-${n}`} className="block">
          <div className={`${i % 2 === 0 ? "arch-frame" : "rounded-sm"} relative border border-brand-border p-3 transition-colors duration-500 group-hover:border-brand-gold`}>
            <div className={`${i % 2 === 0 ? "arch-frame" : "rounded-sm"} overflow-hidden`}>
              <img
                src={p.img}
                alt={p.name}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
            <span className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-brand-gold/70 bg-brand-ivory/90 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
              <ArrowUpRight className="h-4 w-4 text-brand-maroon" strokeWidth={1.5} />
            </span>
          </div>
        </a>
      </div>
      <div className={`md:col-span-6 ${flip ? "md:order-1 md:col-start-1" : "md:col-start-7"}`}>
        <span className="font-display text-7xl font-medium leading-none text-brand-border md:text-8xl" aria-hidden="true">{n}</span>
        <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.35em] text-brand-gold">{p.tag}</p>
        <h3 className="mt-3 font-display text-4xl font-medium text-brand-ink md:text-5xl">{p.name}</h3>
        <p className="mt-6 max-w-md text-base font-light leading-relaxed text-brand-ink/70">{p.desc}</p>
        {(p.price != null || p.stock != null) && (
          <p className="mt-5 text-sm font-medium tracking-wide text-brand-ink/70" data-testid={`product-meta-${n}`}>
            {p.price != null && <span className="text-brand-maroon">₹{p.price}</span>}
            {p.price != null && p.stock != null && <span className="mx-2 text-brand-border">·</span>}
            {p.stock != null && (
              <span className={p.stock > 0 ? "text-brand-ink/60" : "text-brand-maroon"}>
                {p.stock > 0 ? `${p.stock} in stock` : "Currently out of stock"}
              </span>
            )}
          </p>
        )}
        {canBuy && (
          <button
            onClick={() => {
              addToCart(p);
              toast(`${p.name} added to your cart`, { description: "View it from the bag icon on top." });
            }}
            data-testid={`product-add-cart-${n}`}
            className="group mt-6 inline-flex items-center gap-3 rounded-full border border-brand-maroon px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-maroon transition-colors duration-300 hover:bg-brand-maroon hover:text-brand-ivory"
          >
            <ShoppingBag className="h-4 w-4" strokeWidth={1.5} />
            Add to Cart
          </button>
        )}
        {!canBuy && (
          <a
            href={`https://wa.me/919205511502?text=${encodeURIComponent(`Namaste Laxwedeepan, I would like to order ${p.name}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`product-whatsapp-${n}`}
            className="mt-6 inline-flex items-center gap-3 rounded-full border border-brand-maroon px-7 py-3.5 text-[11px] font-medium uppercase tracking-[0.25em] text-brand-maroon transition-colors duration-300 hover:bg-brand-maroon hover:text-brand-ivory"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={1.5} />
            Order on WhatsApp
          </a>
        )}
      </div>
    </motion.article>
  );
};

export const Products = () => {
  const [products, setProducts] = useState(staticProducts);
  const [isFallback, setIsFallback] = useState(false);
  const [checkoutOn, setCheckoutOn] = useState(false);
  useEffect(() => {
    apiFetch("/api/products")
      .then((d) => {
        if (Array.isArray(d) && d.length) setProducts(d);
      })
      .catch(() => setIsFallback(true));
    getConfig().then((c) => setCheckoutOn(!!c.checkout_enabled));
  }, []);

  return (
  <section id="collection" data-testid="products-section" className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-40">
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease: EASE }}
      className="mb-20 md:mb-28"
    >
      <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">The Collection</p>
      <h2 className="mt-6 max-w-3xl font-display text-4xl font-medium leading-tight text-brand-ink md:text-6xl">
        Every offering, <em className="italic text-brand-maroon">one devotion.</em>
      </h2>
    </motion.div>
    {isFallback && (
      <p className="mb-10 text-xs uppercase tracking-[0.2em] text-brand-gold" data-testid="catalogue-fallback-note">
        Showing saved catalogue, latest prices may differ
      </p>
    )}
    {products.map((p, i) => (
      <ProductRow key={p.id || p.name} p={p} i={i} checkoutOn={checkoutOn && !isFallback} />
    ))}
  </section>
  );
};
